import React, {createRef} from 'react';
import PropTypes from 'prop-types';
import JXG from 'jsxgraph';
import {throttle} from 'lodash';

import Toolbar from './Toolbar/Toolbar.react';
import {toCoordsList} from './entities/entities.utils';
import {createCursorPoint, entityTypes} from './board.utils';
import {flavorTypes, colors} from './constants';
import {getAriaDescription, liveRegionActions} from './aria.utils';

import './graphing-input.scss';

const entityTypeNames = Object.keys(entityTypes);

const modes = {
  editing: 'editing', // All entities are selectable and editable.
  guessing: 'guessing', // Only studentGuess
  reviewing: 'reviewing'
};

const labelColor = 'rgba(0, 0, 0, 0.7)';
const labelSize = 14;
const gridColor = '#E1E4E6';
const defaultHeight = 600;
const defaultWidth = 600;
const boundingBoxChangeTimeWindow = 250;

JXG.Options.text.cssDefaultStyle = 'font-family: Lato, Arial, Helvetica, Geneva, sans-serif;';
JXG.Options.text.highlightCssDefaultStyle =
  'font-family: Lato, Arial, Helvetica, Geneva, sans-serif;';

const keymap = {
  left: 'ArrowLeft',
  up: 'ArrowUp',
  right: 'ArrowRight',
  down: 'ArrowDown',
  space: 'Space',
  escape: 'Escape'
};

const keyset = new Set(Object.values(keymap));

/**
 * @typedef {object} Props
 * @property {Array.<string>} allowedEntities Array of entities that are allowed to be added to the graph.
 * @property {Array.<number>} boundingBox Array of limits for the graph's bounding box in order: left, top, right, bottom.
 * @property {string} currentFlavor Current flavor the entity being added. Used to color the `geometryElements` and attached as a propertyerty on the `geometryInstance` for later identification.
 * @property {string} id Unique identifier for the board. Used to tear the down the board and reinitialize if the id changes.
 * @property {Array.<object>} initialEntities Array of entities to initialize the graph with.
 * @property {string} mode Limits the available interactions based on the mode.
 * @property {Function} onChange
 * @property {Function} renderToolbar
 * @property {number} xTickInterval
 * @property {number} yTickInterval
 */

/**
 * @extends {React.Component<Props>}
 */
class BoardWrapper extends React.Component {
  static propTypes = {
    allowedEntities: PropTypes.arrayOf(PropTypes.oneOf(entityTypeNames)),
    boundingBox: PropTypes.arrayOf(PropTypes.number),
    boardWrapperClassName: PropTypes.string,
    className: PropTypes.string,
    currentFlavor: PropTypes.oneOf(Object.values(flavorTypes)),
    id: PropTypes.string.isRequired,
    initialEntities: PropTypes.arrayOf(
      PropTypes.shape({
        type: PropTypes.string.isRequired,
        elements: PropTypes.object.isRequired,
        flavor: PropTypes.oneOf(Object.values(flavorTypes)).isRequired,
        attributes: PropTypes.object
      })
    ),
    mode: PropTypes.oneOf(Object.values(modes)).isRequired,
    onChange: PropTypes.func,
    renderToolbar: PropTypes.func,
    labels: PropTypes.shape({
      top: PropTypes.string,
      right: PropTypes.string,
      bottom: PropTypes.string,
      left: PropTypes.string
    }),
    maxHeight: PropTypes.number,
    maxWidth: PropTypes.number,
    xTickInterval: PropTypes.number,
    yTickInterval: PropTypes.number,
    xAxisLabel: PropTypes.string,
    yAxisLabel: PropTypes.string
  };

  static defaultProps = {
    allowedEntities: entityTypeNames,
    boardWrapperClassName: '',
    boundingBox: [-10, 10, 10, -10],
    className: '',
    currentFlavor: flavorTypes.studentGuess,
    labels: {},
    maxHeight: defaultHeight,
    maxWidth: defaultWidth,
    xAxisLabel: '',
    yAxisLabel: '',
    xTickInterval: 1,
    yTickInterval: 1,
    onChange: () => {}
  };

  constructor(props) {
    super(props);
    this.state = {
      board: null,
      geometryInstances: [],
      partialInstance: null,
      selectedInstance: null,
      cursorPoint: null,
      boundingBoxInitChangeTime: null,
      userPointerDownCount: 0,
      ariaLastAction: '',
      ariaDescription: ''
    };
    this.wrapperRef = createRef();
    this.boardRef = createRef();
  }

  componentDidMount() {
    const {
      id,
      boundingBox,
      initialEntities,
      labels,
      xAxisLabel,
      yAxisLabel,
      xTickInterval,
      yTickInterval,
      mode
    } = this.props;

    const axisArrowSize = 6;
    const board = JXG.JSXGraph.initBoard(id, {
      axis: {
        firstArrow: {
          type: 1,
          highlightSize: axisArrowSize,
          size: axisArrowSize
        },
        lastArrow: {
          type: 1,
          highlightSize: axisArrowSize,
          size: axisArrowSize
        },
        ticks: {
          // This doesn't seem to be working...
          visible: false,
          // ...so we'll just hide everything that's visible by default
          minorHeight: 0,
          majorHeight: 0,
          drawLabels: false
        }
      },
      grid: {
        strokeColor: gridColor,
        strokeOpacity: 1,
        tabindex: null
      },
      showNavigation: false,
      zoom: {
        enabled: true
      },
      pan: {
        enabled: true
      },
      showCopyright: false,
      boundingBox: this.addOffsetToBoundingBox(boundingBox),
      keyboard: {
        enabled: false
      },
      description: ''
    });

    this.createTicksFromBoundingBox(board);
    this.drawAxisLabels(board);

    board.on('down', this.handleBoardDown);
    board.on('up', this.handleBoardUp);
    board.on('move', this.handleBoardMouseMove);
    board.on('boundingbox', this.handleBoundingBoxChange);

    this.boardRef.current.addEventListener('keydown', this.handleKeyboardNavigation);

    let {geometryInstances} = this.state;

    if (initialEntities) {
      geometryInstances = initialEntities.map((entity) =>
        entityTypes[entity.type].create({
          board,
          entity,
          onClick: this.handleInstanceClick
        })
      );
    }

    const ariaDescription = getAriaDescription({
      boundingBox,
      labels,
      xAxisLabel,
      yAxisLabel,
      xTickInterval,
      yTickInterval,
      initialEntities,
      editable: mode !== modes.reviewing
    });

    this.setState({board, ariaDescription});
    this.updateGeometryInstances(geometryInstances);
  }

  componentDidUpdate(prevProps) {
    const {
      boundingBox: prevBoundingBox,
      xTickInterval: prevXTickInterval,
      yTickInterval: prevYTickInterval,
      xAxisLabel: prevXAxisLabel,
      yAxisLabel: prevYAxisLabel
    } = prevProps;
    const {boundingBox, xTickInterval, yTickInterval, xAxisLabel, yAxisLabel} = this.props;
    const {board} = this.state;

    if (prevBoundingBox.some((val, i) => val !== boundingBox[i])) {
      board.setBoundingBox(this.addOffsetToBoundingBox(boundingBox));
      this.createTicksFromBoundingBox(board);
      this.drawAxisLabels(board);
      return;
    }

    if (prevXTickInterval !== xTickInterval || prevYTickInterval !== yTickInterval) {
      this.createTicksFromBoundingBox(board);
      return;
    }

    if (prevXAxisLabel !== xAxisLabel || prevYAxisLabel !== yAxisLabel) {
      this.drawAxisLabels(board);
    }
  }

  componentWillUnmount() {
    if (this.state.board) {
      JXG.JSXGraph.freeBoard(this.state.board);
    }
    global.window.removeEventListener('click', this.handleOutsideClick);
  }

  addOffsetToBoundingBox(boundingBox) {
    const offset = 0.4;
    return boundingBox.map((value, i) => {
      return [0, 3].includes(i) ? value - offset : value + offset;
    });
  }

  createTicksFromBoundingBox(board) {
    const {xTickInterval, yTickInterval, boundingBox} = this.props;
    const [xMin, yMax, xMax, yMin] = boundingBox;

    [board.elementsByName.xAxisTickLine, board.elementsByName.yAxisTickLine].forEach((line) => {
      if (line) {
        board.removeAncestors(line);
      }
    });
    [
      {
        name: 'xAxisTickLine',
        coords: [
          [xMin, 0],
          [xMax, 0]
        ],
        offset: [4, -11],
        usrCoordPos: 1,
        tickInterval: xTickInterval
      },
      {
        name: 'yAxisTickLine',
        coords: [
          [0, yMin],
          [0, yMax]
        ],
        offset: [-6, -1],
        usrCoordPos: 2,
        tickInterval: yTickInterval
      }
    ].forEach(({name, coords, offset, usrCoordPos, tickInterval}) => {
      const tickLine = board.create('line', coords, {
        name,
        fixed: true,
        frozen: true,
        strokeWidth: 0,
        straightFirst: false,
        straightLast: false,
        highlight: false,
        scalable: false,
        tabindex: null
      });
      board.create('ticks', [tickLine], {
        drawLabels: true,
        label: {
          offset,
          color: labelColor,
          highlightColor: labelColor,
          anchorX: 'right',
          fontSize: labelSize
        },
        ticksDistance: 1,
        drawZero: true,
        includeBoundaries: true,
        strokeWidth: 0,
        generateLabelText(current) {
          const pos = Math.round(current.usrCoords[usrCoordPos]) * tickInterval;
          return pos === 0 ? '' : pos;
        }
      });
    });
  }

  drawAxisLabels(board) {
    const {xAxisLabel, yAxisLabel, boundingBox} = this.props;
    const [, yMax, xMax] = boundingBox;

    if (board.elementsByName.xAxisLabel) {
      board.elementsByName.xAxisLabel.setText(xAxisLabel);
      board.elementsByName.xAxisLabel.moveTo([xMax, 0]);

      board.elementsByName.yAxisLabel.setText(yAxisLabel);
      board.elementsByName.yAxisLabel.moveTo([0, yMax]);
      return;
    }

    board.create('text', [xMax, 0, xAxisLabel], {
      name: 'xAxisLabel',
      color: labelColor,
      fontSize: labelSize,
      cssStyle: 'transform: translateY(-10px) translateX(-4px);',
      highlightColor: labelColor,
      fixed: true,
      scalable: false,
      isLabel: true,
      anchorX: 'right',
      tabindex: null
    });

    board.create('text', [0, yMax, yAxisLabel], {
      name: 'yAxisLabel',
      color: labelColor,
      fontSize: labelSize,
      cssStyle: 'transform: translateX(5px) translateY(8px);',
      highlightColor: labelColor,
      fixed: true,
      scalable: false,
      isLabel: true,
      tabindex: null
    });
  }

  updateGeometryInstances(geometryInstances) {
    this.setState({geometryInstances}, () => {
      this.props.onChange(this.state.geometryInstances.map((instance) => instance.entity));
      this.updateAriaDescription();
    });
  }

  updateAriaDescription() {
    const {boundingBox, labels, xAxisLabel, yAxisLabel, xTickInterval, yTickInterval, mode} =
      this.props;

    const {geometryInstances} = this.state;

    const entities = geometryInstances.map(({entity}) => entity);

    const ariaDescription = getAriaDescription({
      boundingBox,
      labels,
      xAxisLabel,
      yAxisLabel,
      xTickInterval,
      yTickInterval,
      entities,
      editable: mode !== modes.reviewing
    });

    this.setState({ariaDescription});
  }

  getMouseCoords(e) {
    const {board} = this.state;
    const [x, y] = board.getUsrCoordsOfMouse(e);
    return [Math.round(x), Math.round(y)];
  }

  removeCursorPoint() {
    const {cursorPoint, partialInstance} = this.state;

    if (!cursorPoint) {
      return;
    }

    if (partialInstance?.handleCursorMove) {
      partialInstance.handleCursorMove(null);
    }

    cursorPoint.remove();
    this.setState({
      cursorPoint: null
    });
  }

  setSelectedInstance = (instance) => {
    instance.highlight();
    this.setState({
      selectedInstance: instance
    });

    this.handleLiveRegionUpdate(liveRegionActions.elementSelected(instance.entity));
  };

  clearSelectedInstance = () => {
    const {selectedInstance} = this.state;
    if (!selectedInstance) {
      return;
    }
    selectedInstance.unhighlight();
    this.setState({
      selectedInstance: null
    });
  };

  updateSelectedInstance = (updatedInstance) => {
    const {selectedInstance, geometryInstances} = this.state;
    const updatedGeometryInstances = geometryInstances.map((instance) => {
      if (instance === selectedInstance) {
        return updatedInstance;
      }
      return instance;
    });
    this.updateGeometryInstances(updatedGeometryInstances);
    this.setSelectedInstance(updatedInstance);
  };

  deleteSelectedInstance = () => {
    const {selectedInstance, geometryInstances} = this.state;

    const updatedGeometryInstances = geometryInstances.filter(
      (instance) => instance !== selectedInstance
    );

    this.updateGeometryInstances(updatedGeometryInstances);
    this.clearSelectedInstance();
    selectedInstance.remove();

    this.handleLiveRegionUpdate(liveRegionActions.elementDeleted(selectedInstance.entity));
  };

  addCursorPoint = () => {
    const {board, cursorPoint} = this.state;

    if (cursorPoint) {
      return;
    }

    const [left, top] = this.props.boundingBox;

    this.setState(
      {
        cursorPoint: createCursorPoint({board, x: left, y: top})
      },
      () => this.handleLiveRegionUpdate(liveRegionActions.cursorPointPlaced([left, top]))
    );
  };

  handleBoardDown = () => {
    this.setState(({userPointerDownCount}) => ({
      userPointerDownCount: userPointerDownCount + 1
    }));
  };

  handleBoundingBoxChange = () => {
    if (this.state.userPointerDownCount === 0 || this.state.boundingBoxInitChangeTime !== null) {
      return;
    }
    this.setState({
      boundingBoxInitChangeTime: Date.now()
    });
  };

  shouldCreatePartialFromBoardUp() {
    const {partialInstance, boundingBoxInitChangeTime, userPointerDownCount} = this.state;

    if (!partialInstance || userPointerDownCount > 1) {
      return false;
    }

    if (!boundingBoxInitChangeTime) {
      return true;
    }

    // When the bounding box changes (which is possible on mobile), we store the time that it changed in state.
    // Here we compare that time with the current time, and if it's less than the `boundingBoxChangeTimeWindow`,
    // we treat the "up" event as the user wanting to create a partial instance.
    // If the difference is greater than the window, we assume the user is intentionally changing the bounding box,
    // so we don't create a partial instance.
    return Date.now() - boundingBoxInitChangeTime < boundingBoxChangeTimeWindow;
  }

  handleBoardUp = (e) => {
    const {partialInstance} = this.state;

    if (this.shouldCreatePartialFromBoardUp()) {
      this.handleCreatePartial(
        partialInstance.entity.type,
        this.getMouseCoords(e),
        e.pointerType !== 'touch'
      );
    }

    this.setState(({userPointerDownCount}) => {
      const nextPointerDownCount = userPointerDownCount - 1;
      const nextState = {
        userPointerDownCount: nextPointerDownCount
      };

      if (nextPointerDownCount === 0) {
        nextState.boundingBoxInitChangeTime = null;
      }

      return nextState;
    });
  };

  handleBoardMouseMove = throttle((e) => {
    const {cursorPoint, partialInstance} = this.state;
    if (cursorPoint && partialInstance) {
      const coords = this.getMouseCoords(e);
      this.moveCursorPoint(coords);
    }
  }, 100);

  moveCursorPoint(coords) {
    const {cursorPoint, partialInstance} = this.state;

    if (cursorPoint && partialInstance) {
      cursorPoint.moveTo(coords);
      if (partialInstance?.handleCursorMove) {
        partialInstance.handleCursorMove(coords);
      }
      this.handleLiveRegionUpdate(liveRegionActions.cursorPointMoved(coords));
    }
  }

  handleInstanceClick = (instance) => {
    const {mode} = this.props;
    const {selectedInstance, partialInstance} = this.state;
    const entityFlavor = instance.entity.flavor;

    if (
      partialInstance ||
      mode === modes.reviewing ||
      (mode === modes.guessing && entityFlavor !== flavorTypes.studentGuess)
    ) {
      return;
    }

    if (selectedInstance) {
      this.state.selectedInstance.unhighlight();
    }

    this.setSelectedInstance(instance);

    this.listenForOutsideClick();
  };

  handleCreatePartial = (type, coords, addCursorPoint = true) => {
    const {currentFlavor} = this.props;
    const {board, partialInstance, cursorPoint} = this.state;

    const nextPartialInstance = entityTypes[type].createPartial({
      board,
      partialInstance,
      coords,
      flavor: currentFlavor
    });

    if (!nextPartialInstance) {
      cursorPoint.errorFeedback();
      return;
    }

    this.setState({partialInstance: nextPartialInstance}, () => {
      if (addCursorPoint) {
        this.addCursorPoint();
      }
      if (this.state.partialInstance?.done) {
        this.handleCompletedPartial();
      } else if (coords) {
        this.handleLiveRegionUpdate(liveRegionActions.partialInstancePointPlaced(coords));
      }
    });

    this.listenForOutsideClick();

    this.boardRef.current.focus();
  };

  handleCompletedPartial = () => {
    const {board, partialInstance, geometryInstances} = this.state;

    if (!partialInstance.done) {
      return;
    }

    const {entity} = partialInstance;
    const newGeometryInstance = entityTypes[entity.type].create({
      board,
      entity,
      onClick: this.handleInstanceClick
    });

    this.updateGeometryInstances([...geometryInstances, newGeometryInstance]);
    this.handleCancelPartial();

    this.handleLiveRegionUpdate(liveRegionActions.elementAdded(entity));
  };

  handleCancelPartial = () => {
    const {partialInstance} = this.state;

    if (!partialInstance) {
      return;
    }

    partialInstance.remove();
    this.removeCursorPoint();
    this.setState({
      partialInstance: null
    });

    this.handleLiveRegionUpdate(liveRegionActions.elementCanceled());
  };

  listenForOutsideClick() {
    // Because we don't know if an event listener is currently attached, we'll remove it before adding it every time
    global.window.removeEventListener('click', this.handleOutsideClick);
    global.window.addEventListener('click', this.handleOutsideClick);
  }

  handleOutsideClick = (e) => {
    if (!global.document.contains(e.target) || this.wrapperRef.current.contains(e.target)) {
      return;
    }

    this.clearSelectedInstance();
    this.handleCancelPartial();

    global.window.removeEventListener('click', this.handleOutsideClick);
  };

  handleKeyboardNavigation = (e) => {
    const keyCode = e.code;

    const {cursorPoint, partialInstance} = this.state;

    if (keyset.has(keyCode)) {
      e.preventDefault();
    }

    if (partialInstance && cursorPoint) {
      // If there's a partial instance, the user is creating an entity
      const coords = cursorPoint.getCoords();

      if (keyCode === keymap.space) {
        e.preventDefault();
        this.handleCreatePartial(partialInstance.entity.type, coords);
        return;
      }

      if (keyCode === keymap.escape) {
        this.handleCancelPartial();
        return;
      }

      let nextCoords;

      const [leftBounds, topBounds, rightBounds, bottomBounds] = this.props.boundingBox;
      const [currentX, currentY] = coords;

      switch (keyCode) {
        case keymap.left:
          nextCoords = [Math.max(currentX - 1, leftBounds), currentY];
          break;
        case keymap.up:
          nextCoords = [currentX, Math.min(currentY + 1, topBounds)];
          break;
        case keymap.right:
          nextCoords = [Math.min(currentX + 1, rightBounds), currentY];
          break;
        case keymap.down:
          nextCoords = [currentX, Math.max(currentY - 1, bottomBounds)];
          break;
        default:
          return;
      }

      e.preventDefault();
      this.moveCursorPoint(nextCoords);
    } else {
      // Otherwise, the user is selecting an existing entity
      const {selectedInstance, geometryInstances} = this.state;

      if (geometryInstances.length === 0) {
        return;
      }

      if (keyCode === keymap.escape) {
        this.clearSelectedInstance();
        return;
      }

      const selectedInstanceIndex = selectedInstance
        ? geometryInstances.indexOf(selectedInstance)
        : null;
      const maxIndex = geometryInstances.length - 1;

      let nextSelectedIndex;

      switch (keyCode) {
        case keymap.left:
        case keymap.up:
          nextSelectedIndex = [null, 0].includes(selectedInstanceIndex)
            ? maxIndex
            : selectedInstanceIndex - 1;
          break;
        case keymap.right:
        case keymap.down:
          nextSelectedIndex = [null, maxIndex].includes(selectedInstanceIndex)
            ? 0
            : selectedInstanceIndex + 1;
          break;
        default:
          return;
      }

      this.handleInstanceClick(geometryInstances[nextSelectedIndex]);
    }
  };

  handleLiveRegionUpdate(lastAction) {
    this.setState({
      ariaLastAction: lastAction
    });
  }

  render() {
    const {
      id,
      allowedEntities,
      renderToolbar,
      labels,
      className,
      boardWrapperClassName,
      maxHeight,
      maxWidth,
      mode
    } = this.props;
    const {top: labelTop, right: labelRight, bottom: labelBottom, left: labelLeft} = labels;
    const {board, partialInstance, selectedInstance} = this.state;

    const describedById = `aria-described-by-${id}`;

    return (
      <section className={className} ref={this.wrapperRef}>
        <Toolbar
          board={board}
          allowedEntities={allowedEntities}
          partialInstance={partialInstance}
          selectedInstance={selectedInstance}
          handleCreatePartial={this.handleCreatePartial}
          handleCancelPartial={this.handleCancelPartial}
          clearSelectedInstance={this.clearSelectedInstance}
          updateSelectedInstance={this.updateSelectedInstance}
          deleteSelectedInstance={this.deleteSelectedInstance}
          renderToolbar={renderToolbar}
        />
        <div className='graphing-input__board-overflow-container'>
          <div className={`graphing-input__board-wrapper ${boardWrapperClassName}`}>
            {labelTop && <div className='graphing-input__board-label'>{labelTop}</div>}
            <div className='graphing-input__board-row'>
              {labelLeft && (
                <div className='graphing-input__board-label graphing-input__board-label--vertical'>
                  {labelLeft}
                </div>
              )}
              <div
                role='application'
                ref={this.boardRef}
                aria-label={`Graph in ${mode} mode`}
                className='graphing-input__board'
                aria-describedby={describedById}
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
              >
                <div aria-hidden>
                  <div
                    id={id}
                    style={{
                      height: Math.min(maxHeight, defaultHeight),
                      width: Math.min(maxWidth, defaultWidth),
                      border: `1px solid ${gridColor}`
                    }}
                  />
                </div>
              </div>
              {labelRight && (
                <div className='graphing-input__board-label graphing-input__board-label--vertical'>
                  {labelRight}
                </div>
              )}
            </div>
            {labelBottom && <div className='graphing-input__board-label'>{labelBottom}</div>}
          </div>
        </div>
        {this.state.ariaLastAction && (
          <div role='status' className='graphing-input-a11y-hidden'>
            <h2>Last action:</h2>
            <p aria-live='assertive'>{this.state.ariaLastAction}</p>
          </div>
        )}
        <p id={describedById} className='graphing-input-described-by'>
          {this.state.ariaDescription}
        </p>
      </section>
    );
  }
}

// Ensures we blow up the board and reinitialize everything if the ID changes
export default function GraphingInput({id, ...rest}) {
  return <BoardWrapper key={id} id={id} {...rest} />;
}

GraphingInput.propTypes = {
  id: PropTypes.string.isRequired,
  attributes: PropTypes.object,
  initialEntities: PropTypes.arrayOf(PropTypes.object)
};

export {modes, colors, flavorTypes, entityTypeNames, toCoordsList};
